import React, { useEffect, useReducer, useRef } from 'react'
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { creativeImportReducer, initialCreativeImportState } from './state'
import { extractRowThumbnails } from './thumbnails'
import { useCreativeImportValidate } from './useCreativeImportValidate'
import { useCreativeImportConfirm } from './useCreativeImportConfirm'
import { useCreativeImportProgress } from './useCreativeImportProgress'
import { UploadStep } from './steps/UploadStep'
import { ReviewStep } from './steps/ReviewStep'
import { OptionsStep } from './steps/OptionsStep'
import { ImportStep } from './steps/ImportStep'
import { SummaryStep } from './steps/SummaryStep'

interface Props {
  open: boolean
  onClose: () => void
}

export const CreativeImportDialog = ({ open, onClose }: Props) => {
  const [state, dispatch] = useReducer(creativeImportReducer, initialCreativeImportState)
  const validateMutation = useCreativeImportValidate()
  const confirmMutation = useCreativeImportConfirm()
  const thumbnailsRef = useRef<Record<number, string>>({})
  thumbnailsRef.current = state.thumbnails
  // Bumped on every new upload/reset so in-flight work from an abandoned
  // upload can't populate the next one's state.
  const uploadTokenRef = useRef(0)

  // Poll only after the backend accepted the confirm — earlier polling would
  // populate `progress` and skip the Import step's confirmation copy.
  const pollingActive =
    (state.step === 'import' || state.step === 'summary') && state.confirmed && !state.confirming
  const progressQuery = useCreativeImportProgress(state.creativeImportID, open && pollingActive)
  const progress = progressQuery.data
  useEffect(() => {
    if (progress) dispatch({ type: 'progressUpdated', progress })
  }, [progress])

  const revokeThumbnails = () => {
    Object.values(thumbnailsRef.current).forEach(url => URL.revokeObjectURL(url))
  }

  const reset = () => {
    uploadTokenRef.current += 1
    revokeThumbnails()
    dispatch({ type: 'reset' })
  }

  // Revoke any outstanding object URLs when the dialog unmounts.
  useEffect(() => revokeThumbnails, [])

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleFile = async (file: File) => {
    revokeThumbnails()
    const token = ++uploadTokenRef.current
    const isStale = () => token !== uploadTokenRef.current
    dispatch({ type: 'validateStart', file })
    try {
      const report = await validateMutation.mutateAsync(file)
      if (isStale()) return
      dispatch({ type: 'validateSuccess', report })
      if (!report.archiveErrors.length) {
        const thumbnails = await extractRowThumbnails(file, report)
        if (isStale()) {
          // Nobody owns these URLs anymore — release them here.
          Object.values(thumbnails).forEach(url => URL.revokeObjectURL(url))
          return
        }
        dispatch({ type: 'thumbnailsLoaded', thumbnails })
      }
    } catch (e: any) {
      if (isStale()) return
      const message =
        e?.response?.data?.message ??
        'The archive could not be validated. Check the file and try again.'
      dispatch({
        type: 'validateError',
        message: Array.isArray(message) ? message.join(' ') : message,
      })
    }
  }

  const handleConfirm = async () => {
    if (state.creativeImportID === null || !state.report) return
    const token = uploadTokenRef.current
    const isStale = () => token !== uploadTokenRef.current
    const rows = state.report.rows
      .filter(row => state.rows[row.rowNumber]?.selected)
      .map(row => ({ rowNumber: row.rowNumber, name: state.rows[row.rowNumber].editedName }))
    dispatch({ type: 'confirmStart' })
    try {
      await confirmMutation.mutateAsync({
        creativeImportID: state.creativeImportID,
        request: { rows, sprintID: state.sprintID ?? undefined },
      })
      if (isStale()) return
      dispatch({ type: 'confirmSuccess' })
    } catch (e: any) {
      if (isStale()) return
      // 409 = already confirmed (double-click/second tab) — just poll.
      if (e?.response?.status === 409) {
        dispatch({ type: 'confirmSuccess' })
        return
      }
      const message =
        e?.response?.data?.message ?? 'The import could not be started. Please try again.'
      dispatch({
        type: 'confirmError',
        message: Array.isArray(message) ? message.join(' ') : message,
      })
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle className="flex items-center justify-between">
        Import Ads
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {state.step === 'upload' && <UploadStep state={state} onFile={handleFile} />}
        {state.step === 'review' && (
          <ReviewStep state={state} dispatch={dispatch} onStartOver={reset} />
        )}
        {state.step === 'options' && (
          <OptionsStep
            state={state}
            dispatch={dispatch}
            onContinue={() => dispatch({ type: 'goToImport' })}
          />
        )}
        {state.step === 'import' && <ImportStep state={state} onConfirm={handleConfirm} />}
        {state.step === 'summary' && (
          <SummaryStep state={state} onImportAnother={reset} onDone={handleClose} />
        )}
      </DialogContent>
    </Dialog>
  )
}
